import { useMemo } from "react";
import { useDispatch, useSelector, type TypedUseSelectorHook } from "react-redux";
import type { AppDispatch, RootState } from "./store";
import { createDefaultUiTabState, type UiTabState } from "./slices/uiSlice";
import type { OhlcTabState } from "./slices/ohlcSlice";
import type { AnalysisTabState } from "./slices/analysisSlice";
import {
  createDefaultReplayTabState,
  type ReplayTabState,
} from "./slices/replaySlice";
import { replayEndIndex } from "@/utils/replayCursor";
import type { Candle } from "@/api/types";

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

/** RBAC: true if the signed-in user has the given capability. Replaces the
 *  legacy `isOwner` gate. See src/api/capabilities.ts + roles_and_access.md. */
export function useCan(cap: string): boolean {
  return useAppSelector((s) => s.me.capabilities.includes(cap));
}

/** The id of the currently-focused tab. With split view the
 *  focused tab is the right pane's active when focusedPaneIdx === 1,
 *  otherwise the left pane's active. Header / Toolbar / Run
 *  Analysis all bind through these "active*" hooks, so changing
 *  focus is what lets the user change TF / run analysis / draw on
 *  the right pane without first moving its tab back to the left.
 *
 *  Components should read state *through* this id (via the
 *  selectors below) rather than storing the id locally — focusing
 *  another pane swaps state out from under them. */
export function useActiveTabId(): string {
  return useAppSelector((s) =>
    s.tabs.focusedPaneIdx === 1 && s.tabs.splitTabId !== null
      ? s.tabs.splitTabId
      : s.tabs.activeTabId,
  );
}

/** Per-tab UI state for the focused tab. Falls back to a fresh
 *  default if the slice hasn't seeded state yet (shouldn't happen —
 *  both the initial tab and addTab seed it — but the fallback keeps
 *  render-time crashes off the table during hot reloads). */
export function useActiveUi(): UiTabState {
  return useAppSelector((s) => {
    const id =
      s.tabs.focusedPaneIdx === 1 && s.tabs.splitTabId !== null
        ? s.tabs.splitTabId
        : s.tabs.activeTabId;
    return s.ui.byTab[id] ?? FALLBACK_UI;
  });
}

export function useActiveOhlc(): OhlcTabState {
  return useAppSelector((s) => {
    const id =
      s.tabs.focusedPaneIdx === 1 && s.tabs.splitTabId !== null
        ? s.tabs.splitTabId
        : s.tabs.activeTabId;
    return s.ohlc.byTab[id] ?? FALLBACK_OHLC;
  });
}

export function useActiveAnalysis(): AnalysisTabState {
  return useAppSelector((s) => {
    const id =
      s.tabs.focusedPaneIdx === 1 && s.tabs.splitTabId !== null
        ? s.tabs.splitTabId
        : s.tabs.activeTabId;
    return s.analysis.byTab[id] ?? FALLBACK_ANALYSIS;
  });
}

// --- tab-id-parameterised variants, for the per-tab Chart instance
// model. Each Chart subscribes to ITS tab's slot directly instead of
// reading through s.tabs.activeTabId, so a hidden chart in tab B
// can still receive its own data updates while tab A is on-screen.

export function useTabUi(tabId: string): UiTabState {
  return useAppSelector((s) => s.ui.byTab[tabId] ?? FALLBACK_UI);
}

/**
 * A tab's OHLC state — truncated at the replay cursor when that tab is in
 * replay.
 *
 * This is the ONE seam the whole replay feature hangs off. `state.ohlc` is
 * read nowhere else in the app, so every downstream consumer (the candle
 * series, MA/RSI, the OHLC header, drawings, the analysis overlays) is already
 * a pure function of this array and sees a shorter one with no change of its
 * own. If a second reader of `state.ohlc` ever appears, it must come through
 * here too — or that surface will quietly show the future during replay.
 *
 * The full (untruncated) candles stay in the store, so stepping the cursor is
 * an array slice rather than a refetch. Components that need the real extent
 * — the replay bar's "bar 1482 / 2310" and its stepping — read
 * `s.ohlc.byTab[tabId]` directly and say so.
 */
export function useTabOhlc(tabId: string): OhlcTabState {
  const ohlc = useAppSelector((s) => s.ohlc.byTab[tabId] ?? FALLBACK_OHLC);
  const replay = useAppSelector((s) => s.replay.byTab[tabId]);
  const active = replay?.active === true;
  const cursorTime = replay?.cursorTime ?? null;

  // useMemo, not a bare object literal: react-redux compares by reference, so
  // returning a fresh object every render would re-render the Chart on every
  // unrelated store change.
  return useMemo(() => {
    if (!active || cursorTime == null) return ohlc;
    const end = replayEndIndex(ohlc.candles, cursorTime);
    if (end >= ohlc.candles.length) return ohlc;
    return {
      ...ohlc,
      candles: end <= 0 ? EMPTY_CANDLES : ohlc.candles.slice(0, end),
    };
  }, [ohlc, active, cursorTime]);
}

/** A tab's replay state, with a stable default so callers needn't null-check. */
export function useTabReplay(tabId: string): ReplayTabState {
  return useAppSelector((s) => s.replay.byTab[tabId] ?? FALLBACK_REPLAY);
}

export function useTabAnalysis(tabId: string): AnalysisTabState {
  return useAppSelector((s) => s.analysis.byTab[tabId] ?? FALLBACK_ANALYSIS);
}

// Stable fallback objects so the selectors don't return a fresh
// reference on every render when state is briefly missing — react-
// redux would treat that as a change and force a re-render.
const FALLBACK_UI: UiTabState = createDefaultUiTabState();
const FALLBACK_REPLAY: ReplayTabState = createDefaultReplayTabState();
/** Shared empty array for a cursor parked before the first loaded bar —
 *  a fresh `[]` each render would defeat the useMemo above. */
const EMPTY_CANDLES: Candle[] = [];
const FALLBACK_OHLC: OhlcTabState = {
  candles: [],
  metadata: null,
  tickProfile: [],
  filesLoaded: [],
  status: "idle",
  error: null,
  failedFor: null,
  oldestDataTime: null,
  nextLoadMonth: null,
  isLoadingMore: false,
  noMoreData: false,
  loadMoreFailed: false,
  emptyStreakCount: 0,
  prependSerial: 0,
  lastPrependCount: 0,
  fetchSerial: 0,
  loadedFor: null,
  lastFetchAt: 0,
};
const FALLBACK_ANALYSIS: AnalysisTabState = {
  rawBins: [],
  liveBins: [],
  directionalMove: null,
  liquidityRanges: [],
  leastTouchRanges: [],
  fromCache: false,
  status: "idle",
  error: null,
  isLive: false,
  liveStartedAt: null,
  liveTickCount: 0,
  analyzedFor: null,
};
